import Vue from 'vue';
import Component from 'vue-class-component';
import {Inject, Prop, Watch} from 'vue-property-decorator';
import Moment from 'moment';
import { END_REPEAT, EVENTS_STATUS_DEFAULT, FEQ_DAY_OF_MONTH, FEQ_DAY_POSITION, FEQ_LIST_MONTHYEAR, FEQ_LIST_WEEKDAY, FREQUENCY
} from '@/core/values/calendar.values';
import {STORE, Store} from '@/core/store/core.store';
import KEYS from '@/values/keys';
import {CALENDAR_EVENTS} from '@/content/values/events';
import {  isBefore, format, parse, isEqual, isAfter, addHours } from 'date-fns'
import EdInputModel from '@/models/ed-input.model';
import {maxLength, required} from 'vuelidate/lib/validators';

@Component({})
export default class EdCalendarCreateEvents extends Vue {

  /**
   * Splash
   */
  @Inject() EduacAppSetLoading;

  /* Props */
  @Prop() readonly params: any;

  /* Data */
  //-- Frequency  (First Row UI)
  public frequencies: any[] = FREQUENCY;
  public frequency: any = FREQUENCY[0];
  public freqIconEnable: boolean = false;
  public freqRecInterval: string = '1';
  public freqEveryWeekDay: boolean = false;  // just freq = day (disable at the moment)

  //-- WEEK & MONTH & YEAR  (Second Row UI)
  public freqIsByMonthDay: boolean = true;  // Only for monthly (switch)
  public freqRecByType: [];  //-- List Items: Week (Sunday, Monday) Month (1,2,...31) Year (January, ... December)

  public freqDayPositionItems: any[] = FEQ_DAY_POSITION;
  public freqDayPosition: any = '';  //-- First, Second ...
  public freqDayPositionOnWeek: any = '';   // Sunday, Monday ...

  //-- EndRepeat  (Third Row UI)
  public endRepeatItems: any[] = END_REPEAT;
  public endRepeat: string = 'NEVER';
  public freqRecCount: string = '';
  public freqUntilDate: any = '';
  public freqEndRepeatDateShowMenu: any = '';

  //-- v-form
  public valid: boolean = false;
  public newItem: Item = { calId: null, id: null, summary: '',description: '', startDate: '', endDate: '', type: 'event'};
  public isValidDescription: boolean = false;

  public initCategories: string[] = [];
  public searchCategory: any = null;

  public initAttendees: string[] = [];
  public searchAttendees: any = null;
  public currentDate: Date = null;
  public isValidSummary: boolean = false;

  //-- -----------------------------------------------------------------------------------------------------
  //-- Computed
  //-- -----------------------------------------------------------------------------------------------------
  get description(): any {
    return EdInputModel('description', this.item.description ?? '', {maxLength: maxLength(100)});
  }

  set description(newVal: any) {
    this.isValidDescription = newVal.valid;
    this.item.description = newVal.value;
  }

  get calendars(): any[] {
    if (this.editing){
      return Store.state.CalendarStore.calendars.map(cal => { return { text: cal.info.name, value: cal.info.calendarId } });
    }else{
      return Store.state.CalendarStore.calendars.filter(c => c.info.canEdit).map(cal => { return { text: cal.info.name, value: cal.info.calendarId } });
    }
  }

  get summary(): any {
    return EdInputModel('summary', this.item.summary ?? '', {required, maxLength: maxLength(60)});
  }

  set summary(newVal: any) {
    this.isValidSummary = newVal.valid;
    this.item.summary = newVal.value;
  }

  get item(): any {
    if (this.params.item){
      if (this.params.item.allDay) {
        this.params.item.startDate = Moment(this.params.item.start).toDate();
        this.params.item.endDate = Moment(this.params.item.end).hour(23).minute(59).toDate();
        this.params.item.startDate && this.params.item.endDate; //don't remove -> read lines neet to be executed
      }

      return this.params.item;
    }
    return this.newItem;
  }

  get editing(){
    return this.item.id != null;
  }

  get canEdit(){
    return this.editing ? this.item.canEdit : true;
  }

   get enableToSave() : boolean {
     return this.canEdit && this.valid && this.isValidDescription && this.isValidSummary;
  }

  private isValid() {
    const isValidDescription = (this.item.description || '').length <= 100;

    const startDate = this.item.startDate;
    const endDate = this.item.endDate;

    const isValidStartDate= this.isNoNull(this.item.startDate) && isBefore(startDate, endDate);
    const isValidEndDate= this.isNoNull(this.item.endDate) && isBefore(startDate, endDate);


    const nowDate = new Date();
    const formattedNow = format(nowDate, 'yyyy-MM-dd hh:mm a');
    const currentDate = parse(formattedNow, 'yyyy-MM-dd hh:mm a', new Date());

    let initialDateIsAfterCurrentDate = isAfter(startDate, currentDate) || isEqual(startDate, currentDate);

    this.valid = this.isNoNull(this.item.calId) &&
    this.isNoNull(this.item.summary) &&
    isValidStartDate && isValidEndDate && isValidDescription && initialDateIsAfterCurrentDate;
  }

  private isNoNull(value: any | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'number') {
      return !isNaN(value);
    }

    return Boolean(value);
  }

  @Watch('item', { deep: true })
  async onItemChange() {
    this.isValid();
  }

  @Watch('item.startDate', { deep: true })
  onStartDateChange() {
    this.revalidateField('datepickerEndDate');
  }

  @Watch('item.endDate', { deep: true })
  onEndDateChange() {
    this.revalidateField('datepickerStartDate');
  }

  private revalidateField(refName: string) {
    const parentComponent = this.$refs[refName] as any;

    if (parentComponent?.$children) {
      parentComponent.$children.forEach((child: any) => {
        if (child?.$children) {
          child.$children.forEach((nestedChild: any) => {
            if (typeof nestedChild.validate === 'function') {
              nestedChild.validate();
            }
          });
        }
      });
    }
  }

  public rules(control:string, limitInit?:number, limitEnd?:number){
    let rules = [];
    switch (control) {
      case 'startDate':
        rules = [
          _ => {
            return this.canEdit
          },
          value => {
            if (value) return true
            return this.$t('calendar.data.fieldRequired')
          },
          value => {
            const start = parse(value, 'yyyy-MM-dd hh:mm a', new Date());
            const now = new Date();
            const formattedNow = format(now, 'yyyy-MM-dd hh:mm a');
            const currentDate = parse(formattedNow, 'yyyy-MM-dd hh:mm a', new Date());

            if (isBefore(start, currentDate)) {
              return this.$t('calendar.data.dateErrorNoPast');
            }

            if (this.item.endDate && (isAfter(start, this.item.endDate) || isEqual(start, this.item.endDate))) {
              return this.$t('calendar.data.dateErrorLimit');
            }
            return true;
          },
        ]

        break;
      case 'endDate':
        rules = [
          _ => {
            return this.canEdit
          },
          value => {
            if (value) return true
            return this.$t('calendar.data.fieldRequired')
          },
          value => {
            const end = parse(value, 'yyyy-MM-dd hh:mm a', new Date());
            const now = new Date();
            const formattedNow = format(now, 'yyyy-MM-dd hh:mm a');
            const currentDate = parse(formattedNow, 'yyyy-MM-dd hh:mm a', new Date());


            if (isBefore(end, currentDate)) {
              return this.$t('calendar.data.dateErrorNoPastEnd');
            }

            if (this.item.startDate && (isBefore(end, this.item.startDate) || isEqual(end, this.item.startDate))) {
              return this.$t('calendar.data.dateErrorLimit');
            }

            return true;
          },
        ]
        break;
      case 'required':
        rules = [
          value => {
            if (value) return true
            return this.$t('calendar.data.fieldRequired')
          },
        ]
        break;
      case 'int':
        rules = [
          v => (!isNaN(parseFloat(v)) && v >= limitInit && v <= limitEnd),
        ]
        break;
      case 'summary':
          rules = [
            value => {
              if (value) return true
              return this.$t('calendar.data.fieldRequired')
            },
            value => {
              if (value?.length <= 60) return true
              return this.$t('calendar.data.fieldLimit', {amount: 60})
            },
          ]
        break;
      case 'description':
        rules = [
          value => {
            if (!value) return true
            if (value.length <= 100) return true
            return this.$t('calendar.data.fieldLimit', {amount: 100})
          },
        ]
        break;
      case 'calendar':
        rules = [
          value => {
            if (value) return true
            return this.$t('calendar.data.fieldRequired')
          }
        ]
        break;
      case 'location':
        rules = [
          value => {
            if (!value) return true
            if (value && value.length <= 100) return true
            return this.$t('calendar.data.fieldLimit', {amount: 100})
          },
        ]
        break;
    }
    return rules;
  }

  //-- -----------------------------------------------------------------------------------------------------
  //-- Methods
  //-- -----------------------------------------------------------------------------------------------------
  closeModal() {
    this.$EdModal.closeModal();
  }

  public enableFreqIcon() {
    this.freqIconEnable = !this.freqIconEnable;
    if (!this.freqIconEnable && !this.editing){
      this.freqRecInterval = '1';
      this.freqEveryWeekDay = false;
      this.freqRecByType = [];
      this.freqDayPosition = '';
      this.freqDayPositionOnWeek = '';
    }
  }

  public cleanFreqEvery(){
    if (this.freqEveryWeekDay){
      this.freqRecInterval = '1';
    }
  }

  get showFreqIsByMonthDay(): boolean{
    return this.freqIconEnable &&
          this.frequency == 'MONTHLY';
  }

  public cleanIsByMonthDay(){
    if (this.freqIsByMonthDay){
      this.freqDayPosition = '';
      this.freqDayPositionOnWeek = '';
    }else{
      this.freqRecByType = [];
    }
  }

  get freqEveryListItems(): any[]{
    if (this.frequency){
      if (this.frequency == 'DAILY'){
        return [];
      }else if (this.frequency == 'WEEKLY'){
        return FEQ_LIST_WEEKDAY;
      }else if (this.frequency == 'MONTHLY'){
        const endOfMonth = parseInt(Moment(this.params.date).endOf('month').format('DD'));
        let days = [];
        for (let i = 1; i <= endOfMonth; i++) {
          days.push({ key: i.toString(), value: i.toString() },);
        }
        return days;
      }else if (this.frequency == 'YEARLY'){
        return FEQ_LIST_MONTHYEAR;
      }
    }
    return [];
  }

  public changeEndRepeat(){
    if (this.endRepeat == 'NEVER'){
      this.freqRecCount = '';
      this.freqUntilDate = '';
    }
    if (this.endRepeat == 'AFTER'){
      this.freqUntilDate = '';
    }
    if (this.endRepeat == 'ON_DATE'){
      this.freqRecCount = '';
    }
  }

  public showModal( body: any, type: string) {
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('calendar.data.calendar'),
      body: body,
      type: type
    });
  }

  createEventTask(data: any) {
    this.EduacAppSetLoading(true);
    this.$store.dispatch(STORE.CALENDAR.ACTIONS.CREATE_ITEM, data).then(() => {
      this.$edEventBus.$emit(CALENDAR_EVENTS.CREATE_ITEM, data);
      this.closeModal();
    })
    .catch(()=>{
      this.showModal(this.$i18n.t('calendar.data.unSuccesChanges'), KEYS.EVENT_TYPES.DANGER);
      this.EduacAppSetLoading(false);
    });
  }

  updateEventTask(data: any) {
    this.EduacAppSetLoading(true);
    this.$store.dispatch(STORE.CALENDAR.ACTIONS.UPDATE_ITEM, data).then(() => {
      this.$edEventBus.$emit(CALENDAR_EVENTS.UPDATE_ITEM, data);
      this.closeModal();
    }).catch((err) => {
      this.showModal(this.$i18n.t('calendar.data.unSuccesChanges'), KEYS.EVENT_TYPES.DANGER);
      this.EduacAppSetLoading(false);
    });
  }

  public deleteItem() {
    this.EduacAppSetLoading(true);
    this.$store.dispatch(STORE.CALENDAR.ACTIONS.REMOVE_ITEM,
        {item: this.item, calStartDate: this.params.startDate, calEndDate: this.params.endDate}).then(() => {
      this.$edEventBus.$emit(CALENDAR_EVENTS.REMOVE_ITEM, this.item);
      this.closeModal();
    }).catch((err) => {
      this.showModal(this.$i18n.t('calendar.data.unSuccesChanges'), KEYS.EVENT_TYPES.DANGER);
      this.EduacAppSetLoading(false);
    });
  }

  //-- -----------------------------------------------------------------------------------------------------
  //-- Frequency
  public setFrequency(frequency: any) {
    //-- Clean data (ON)
    this.freqRecByType = [];
  }

  public isNumber(evt) {
    evt = (evt) ? evt : window.event;
    let charCode = (evt.which) ? evt.which : evt.keyCode;
    if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
      evt.preventDefault();
    } else {
      return true;
    }
  }

  get showFreqEveryListItems(): boolean {
    if (this.frequency == 'MONTHLY'){
      return this.freqIconEnable && this.freqIsByMonthDay;
    }else{
      return this.freqIconEnable && this.frequency != 'DAILY';
    }
  }

  get showEndRepeat(): boolean {
    return this.frequency.key !== '';
  }

  get freqDayPositionOnWeekItems(){
    if(this.freqIconEnable && this.frequency == 'MONTHLY'){
      return FEQ_DAY_OF_MONTH;
    }else{
      return FEQ_LIST_WEEKDAY;
    }
  }

  get showFreqDayPosition(): boolean {
    return this.freqIconEnable &&
        (this.frequency == 'YEARLY' ||
            (this.frequency == 'MONTHLY' && !this.freqIsByMonthDay));
  }

  descFrequency(){
    if (this.frequency == 'DAILY'){
      return this.$i18n.t('calendar.data.everyDay');
    }else if (this.frequency == 'WEEKLY'){
      return this.$i18n.t('calendar.data.everyWeek');
    }else if (this.frequency == 'MONTHLY'){
      return this.$i18n.t('calendar.data.everyMonth');
    }else if (this.frequency == 'YEARLY'){
      return this.$i18n.t('calendar.data.everyYear');
    }
  }

  //-- -----------------------------------------------------------------------------------------------------
  //-- Hooks
  //-- -----------------------------------------------------------------------------------------------------
  public async saveItem() {
    this.revalidateField('datepickerStartDate');
    this.isValid();
    if (!this.valid) {
      return;
    }
    
    let fi = format(this.item.startDate, 'yyyy-MM-dd') + 'T' + format(this.item.startDate, 'HH:mm') + ':00Z';
    let ff = format(this.item.endDate, 'yyyy-MM-dd') + 'T' + format(this.item.endDate, 'HH:mm') + ':00Z';
    let data: any = {
      canEdit: this.canEdit,
      type: this.item.type,
      calId: this.item.calId,
      calStartDate: this.params.startDate,
      calEndDate: this.params.endDate,
      uid: this.item.id,
      description: this.item.description,
      summary: this.item.summary,
      startDate: fi,
      endDate: ff,
      location: (this.item.location.link.trim() === '') ? null : this.item.location,
      categories: (this.item.categories && this.item.categories.length>0 && this.item.categories[0].trim() !== '') ? this.item.categories : null,
      attendees: (this.item.attendees && this.item.attendees.length>0 && this.item.attendees[0].trim() !== '') ? this.item.attendees : null,

      status: EVENTS_STATUS_DEFAULT,
      frequency: (this.frequency.key !== undefined) ? null : this.frequency,
      freqRecInterval: this.freqRecInterval,

      freqRange: this.endRepeat,
      freqUntilDate: this.freqUntilDate,
      freqRecCount: this.freqRecCount,

      freqRecByType: this.freqRecByType && this.freqRecByType.length == 0 ? [] : this.freqRecByType,
      freqEveryWeekDay: this.freqEveryWeekDay,
      freqDayPosition: this.freqDayPosition,
      freqDayPositionOnWeek: this.freqDayPositionOnWeek
    }
    if (this.editing) {
      this.updateEventTask(data);
    } else {
      this.createEventTask(data);
    }
  }

  created() {
    let tdate = new Date();
    tdate.setSeconds(0,0);
    this.currentDate = tdate;

    let event : any = this.item;

    if(!event.location){
      event['location'] = {};
      event.location['link'] = '';
    }
    if(this.editing) {
      event.startDate = new Date(event.start);
      event.endDate = new Date(event.end);
      this.freqRecByType = [];

      //-- Set frequency on UI
      if (event.frequency){

        //-- First Row
        this.frequency = event.frequency;
        if(event.freqRecInterval == -1){
          this.freqRecInterval = '1';
        }else{
          this.freqRecInterval = event.freqRecInterval;
          this.freqIconEnable = true;
        }

        //-- Second Row
        if (event.freqEveryWeekDay){
          this.freqEveryWeekDay = event.freqEveryWeekDay;
          this.freqIconEnable = true;
        }
        if (event.freqRecByType){
          this.freqRecByType = event.freqRecByType;
          this.freqIconEnable = true;
          if (this.frequency === 'MONTHLY'){
            this.freqIsByMonthDay = true;
          }
        }
        if (event.freqDayPosition){
          this.freqIconEnable = true;
          this.freqDayPosition = event.freqDayPosition;
          this.freqDayPositionOnWeek = event.freqDayPositionOnWeek;
          if (this.frequency === 'MONTHLY'){
            this.freqIsByMonthDay = false;
          }
        }

        //-- Third Row
        this.endRepeat = END_REPEAT[0].key;
        if (event.freqRecCount && event.freqRecCount!=-1){
          this.freqRecCount = event.freqRecCount;
          this.endRepeat = END_REPEAT[1].key;
        }
        if (event.freqUntilDate){
          this.freqUntilDate = event.freqUntilDate.substring(0, event.freqUntilDate.indexOf('T'));
          this.endRepeat = END_REPEAT[2].key;
        }
      }
    }else{
      this.item.startDate = new Date();
      this.item.endDate = addHours(new Date(), 1);
    }
  }

  private validateEmail(value) {
    let validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (value.match(validRegex)) {
      return true;
    } else {
      return false;
    }
  }

  @Watch('searchAttendees')
  validateAttendees() {
    if (!this.searchAttendees && this.item.attendees && this.item.attendees.length>0){
      if (!this.validateEmail(this.item.attendees[this.item.attendees.length-1])){
        this.item.attendees.pop();
      }
    }
  }
}

//-- Model of Events & Tasks
interface Item {
  calId: number;
  id: number;
  summary: string;
  description?: string;
  startDate: string;
  endDate: string;
  type: string;
  location?: Location;
}

interface Location {
  link: string;
}
